import React from "react";
import { motion } from "framer-motion";

const specs = [
  { label: "Footprint", value: "500", unit: "m²" },
  { label: "Stud Height", value: "9.5", unit: "m" },
  { label: "Region First", value: "South", unit: "Island" },
  { label: "Use", value: "Multi", unit: "Purpose" },
];

const STUDIO_IMG =
  "https://customer-assets.emergentagent.com/job_paradise-central/artifacts/xcapr237_DJI_0617.JPG";

export default function Studio() {
  return (
    <section
      id="studio"
      data-testid="studio-section"
      className="relative py-28 md:py-40 border-t border-white/5 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-[#E2B464]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E2B464]">
                03 — Wet Weather Cover
              </span>
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              The South Island's{" "}
              <span className="italic text-[#E2B464]">missing link.</span>
            </h2>
            <p className="mt-8 text-zinc-400 leading-relaxed text-base max-w-md">
              Paradise Studios, a 500 m² wet-weather cover stage at Victoria
              Flats. So when Otago decides to give you four seasons in an
              afternoon, the schedule never moves.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {specs.map((s, i) => (
                <div
                  key={s.label}
                  className={`bg-[#0a0a0a] p-5 ${
                    i === specs.length - 1 && specs.length % 2 === 1 ? "col-span-2" : ""
                  }`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                    {s.label}
                  </p>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-display text-2xl md:text-3xl text-white font-light tracking-tight">
                      {s.value}
                    </span>
                    <span className="font-mono text-xs text-[#E2B464]">{s.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-7 relative"
          >
            <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden border border-white/10">
              <img
                src={STUDIO_IMG}
                alt="Paradise Studios wet-weather cover building at Victoria Flats — aerial view"
                className="absolute inset-0 w-full h-full object-cover scale-100"
                style={{ objectPosition: "50% 100%" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E2B464]">
                    Stage A · 500 m² Wet-Weather Cover
                  </p>
                  <p className="font-display text-xl md:text-2xl text-white mt-2 tracking-tight">
                    First of its scale · South Island
                  </p>
                </div>
                <div className="hidden md:block font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                  03 / 06
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Interior · In Production gallery */}
        <div className="mt-16 md:mt-24">
          <div className="flex items-end justify-between mb-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E2B464]">
              Inside · In Production
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hidden md:block">
              Stage A · Working set
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {[
              {
                src: "https://customer-assets.emergentagent.com/job_paradise-central/artifacts/ag1mpesw_IMG_4805.jpeg",
                alt: "Set build inside the cover stage — brick storefront facade with scissor lift",
                tag: "Set Build",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_paradise-central/artifacts/z688ra1d_IMG_4826.jpeg",
                alt: "Crew behind the camera on a working day inside the stage",
                tag: "Crew · Camera",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_paradise-central/artifacts/tkk9yn51_IMG_4833.jpeg",
                alt: "Night lighting setup with crew silhouettes around an illuminated panel",
                tag: "Night Shoot",
              },
            ].map((img, i) => (
              <motion.div
                key={img.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`studio-interior-${i}`}
                className="group relative aspect-[4/3] overflow-hidden border border-white/10 hover:border-[#E2B464]/60 transition-colors"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-200 bg-black/40 backdrop-blur px-2.5 py-1 border border-white/10">
                  {img.tag}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
